import React from 'react';
import Icon from '../../../components/AppIcon';
import { useNavigate } from 'react-router-dom';

const STAGES = [
  {
    id: 1,
    label: 'Submitted',
    description: 'Application received and queued for review',
    icon: 'CheckCircle',
    timeline: 'Immediate',
  },
  {
    id: 2,
    label: 'Under Review',
    description: 'Our team is reviewing your documents and information',
    icon: 'Search',
    timeline: '1–2 business days',
  },
  {
    id: 3,
    label: 'Eligibility Check',
    description: 'Identity verification and SocialCents Score assessment',
    icon: 'ShieldCheck',
    timeline: '2–3 business days',
  },
  {
    id: 4,
    label: 'Decision',
    description: 'Final approval or conditional offer issued',
    icon: 'Sparkles',
    timeline: '3–5 business days',
  },
];

const ApplicationStatusTracker = ({ applicationId }) => {
  const navigate = useNavigate();
  const currentStage = 1;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ background: '#0F172A' }}>
      <div className="w-full max-w-lg">
        {/* Success Header */}
        <div className="text-center mb-10">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5"
            style={{ background: 'linear-gradient(135deg, rgba(13,148,136,0.2), rgba(29,78,216,0.2))', border: '2px solid rgba(20,184,166,0.4)' }}
          >
            <Icon name="CheckCircle" size={36} color="#14B8A6" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            Application Submitted!
          </h1>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
            Your SocialCents Card application is now under review.
          </p>
          {applicationId && (
            <div
              className="inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded-full"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <Icon name="Hash" size={12} color="rgba(255,255,255,0.4)" />
              <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', fontFamily: 'JetBrains Mono, monospace' }}>
                {applicationId?.slice(0, 8)?.toUpperCase()}
              </span>
            </div>
          )}
        </div>

        {/* Status Tracker */}
        <div
          className="rounded-2xl p-6 mb-6"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <h2 className="text-sm font-semibold mb-5" style={{ color: 'rgba(255,255,255,0.6)' }}>Application Progress</h2>
          <div className="space-y-0">
            {STAGES?.map((stage, idx) => {
              const isCompleted = currentStage > stage?.id;
              const isActive = currentStage === stage?.id;
              const isPending = currentStage < stage?.id;
              return (
                <div key={stage?.id} className="flex gap-4">
                  {/* Left: icon + line */}
                  <div className="flex flex-col items-center">
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all"
                      style={{
                        background: isCompleted
                          ? 'linear-gradient(135deg,#0d9488,#1d4ed8)'
                          : isActive
                          ? 'rgba(20,184,166,0.15)'
                          : 'rgba(255,255,255,0.04)',
                        border: isActive
                          ? '2px solid #14B8A6'
                          : isCompleted
                          ? 'none' :'2px solid rgba(255,255,255,0.1)',
                        boxShadow: isActive ? '0 0 14px rgba(20,184,166,0.3)' : 'none',
                      }}
                    >
                      <Icon
                        name={isCompleted ? 'Check' : stage?.icon}
                        size={15}
                        color={isCompleted ? '#fff' : isActive ? '#14B8A6' : 'rgba(255,255,255,0.25)'}
                      />
                    </div>
                    {idx < STAGES?.length - 1 && (
                      <div
                        className="w-px flex-1 my-1"
                        style={{
                          background: isCompleted
                            ? 'linear-gradient(180deg,#0d9488,#1d4ed8)'
                            : 'rgba(255,255,255,0.07)',
                          minHeight: '32px',
                        }}
                      />
                    )}
                  </div>
                  {/* Right: content */}
                  <div className="pb-6 flex-1">
                    <div className="flex items-center justify-between">
                      <p
                        className="text-sm font-semibold"
                        style={{ color: isActive ? '#14B8A6' : isCompleted ? '#fff' : 'rgba(255,255,255,0.3)' }}
                      >
                        {stage?.label}
                        {isActive && (
                          <span
                            className="ml-2 text-xs px-2 py-0.5 rounded-full"
                            style={{ background: 'rgba(20,184,166,0.15)', color: '#14B8A6', border: '1px solid rgba(20,184,166,0.3)' }}
                          >
                            Current
                          </span>
                        )}
                      </p>
                      <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.25)' }}>{stage?.timeline}</span>
                    </div>
                    <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.35)' }}>
                      {stage?.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Info */}
        <div
          className="flex items-start gap-3 p-4 rounded-xl mb-6"
          style={{ background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.2)' }}
        >
          <Icon name="Bell" size={15} color="#fbbf24" className="flex-shrink-0 mt-0.5" />
          <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.55)', lineHeight: '1.5' }}>
            You'll receive email notifications at each stage. Estimated total review time: <strong style={{ color: 'rgba(255,255,255,0.75)' }}>3–5 business days</strong>.
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => navigate('/social-cents-card-screen')}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-90"
            style={{ background: 'linear-gradient(135deg,#0d9488,#1d4ed8)', color: '#fff' }}
          >
            <Icon name="CreditCard" size={16} />
            Back to Card Page
          </button>
          <button
            onClick={() => navigate('/user-dashboard')}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all"
            style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            <Icon name="LayoutDashboard" size={16} />
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApplicationStatusTracker;
